import type { OpenArtifactInput } from "@t3tools/contracts";

import { t } from "~/i18n";
import { currentServerToken } from "./serverToken";
import {
  isLoopbackHostname,
  readPrimaryEnvironmentTarget,
  resolvePrimaryEnvironmentHttpUrl,
} from "./target";

/**
 * Whether the server runs on this machine. Opening a file there means the OS
 * opening it; a team server sends the file to the member's browser.
 */
export function serverRunsOnThisMachine(): boolean {
  const target = readPrimaryEnvironmentTarget();
  return target !== null && isLoopbackHostname(new URL(target.target.httpBaseUrl).hostname);
}

function fileNameFrom(contentDisposition: string | null): string | null {
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition ?? "")?.[1];
  return encoded === undefined ? null : decodeURIComponent(encoded);
}

const INLINE_MIME_TYPES = new Map([
  ["pdf", "application/pdf"],
  ["png", "image/png"],
  ["jpg", "image/jpeg"],
  ["jpeg", "image/jpeg"],
  ["gif", "image/gif"],
  ["webp", "image/webp"],
  ["avif", "image/avif"],
  ["bmp", "image/bmp"],
  ["mp3", "audio/mpeg"],
  ["m4a", "audio/mp4"],
  ["wav", "audio/wav"],
  ["ogg", "audio/ogg"],
  ["mp4", "video/mp4"],
  ["webm", "video/webm"],
  ["txt", "text/plain; charset=utf-8"],
  ["md", "text/plain; charset=utf-8"],
]);
const MAX_INLINE_BYTES = 200 * 1024 * 1024;

export function inlineArtifactMimeType(href: string): string | null {
  const path = href.split(/[?#]/, 1)[0]!;
  const extension = /[^/]+\.([A-Za-z0-9]+)$/.exec(path)?.[1]?.toLowerCase();
  return extension === undefined ? null : (INLINE_MIME_TYPES.get(extension) ?? null);
}

function retainObjectUrl(tab: Window, href: string): void {
  const release = () => {
    clearInterval(interval);
    window.removeEventListener("pagehide", release);
    URL.revokeObjectURL(href);
  };
  const interval = setInterval(() => {
    if (tab.closed) release();
  }, 1000);
  window.addEventListener("pagehide", release, { once: true });
}

/** The href carries the client-only type hint and fragment for a short link. */
export async function downloadArtifact(input: OpenArtifactInput, href?: string): Promise<void> {
  const path = href ?? ("linkId" in input ? `/f/${input.linkId}` : input.path);
  const type = inlineArtifactMimeType(path);
  // Reserve the tab before fetching: Safari requires window.open in the click task.
  const tab = type === null ? null : window.open("", "_blank");
  try {
    if (tab !== null) tab.document.body.textContent = t("environments/loadingFile_5nee67");
    const response = await fetchServerFile(
      resolvePrimaryEnvironmentHttpUrl("/api/artifacts/download", {
        projectId: input.projectId,
        ...("linkId" in input ? { link: input.linkId } : { path: input.path }),
      }),
      "linkId" in input,
    );
    if (tab !== null && Number(response.headers.get("content-length")) > MAX_INLINE_BYTES) {
      tab.close();
      downloadBlob(await response.blob(), response, path);
      return;
    }
    const blob = await response.blob();
    if (tab === null || type === null || blob.size > MAX_INLINE_BYTES) {
      tab?.close();
      downloadBlob(blob, response, path);
      return;
    }
    if (tab.closed) return;
    // Only allowlisted types may run under the workspace's blob origin.
    const objectUrl = URL.createObjectURL(new Blob([blob], { type }));
    try {
      tab.location.href = objectUrl + (path.includes("#") ? path.slice(path.indexOf("#")) : "");
      tab.opener = null;
      retainObjectUrl(tab, objectUrl);
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      throw error;
    }
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/**
 * Downloads a file the server answers to its server token alone — an
 * artifact, an attachment a message links — which a link the browser follows
 * by itself cannot send. `path` names the file when the answer does not.
 */
export async function downloadServerFile(url: string, path: string): Promise<void> {
  const response = await fetchServerFile(url);
  downloadBlob(await response.blob(), response, path);
}

async function fetchServerFile(url: string, useResponseMessage = false): Promise<Response> {
  const token = currentServerToken();
  const response = await fetch(
    url,
    token === null ? {} : { headers: { authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    const message =
      useResponseMessage && (response.status === 400 || response.status === 404)
        ? (await response.text()).trim()
        : "";
    throw new Error(
      message ||
        (response.status === 404
          ? t("environments/theFileWasNot_1x7bno")
          : t("environments/theFileCouldNot_ewccmu")),
    );
  }
  return response;
}

function downloadBlob(blob: Blob, response: Response, path: string): void {
  const name =
    fileNameFrom(response.headers.get("content-disposition")) ??
    decodeURIComponent(path.split(/[?#]/, 1)[0]!.split("/").pop() ?? "");
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  // The click hands the blob to the browser's download, which reads it after
  // this task returns; revoking at once can cancel it.
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
}
